<template>
    <form v-if="!loading" @submit.prevent="itemUUID ? requestUpdate() : requestCreate()">
        <div class="mx-0 mb-4 overflow-auto">
            <ul class="nav nav-tabs flex-nowrap">
                <li
                    v-for="(item, index) of entryTypeDdl"
                    :key="index"
                    class="nav-item"
                >
                    <a
                        href="javascript:void(0);"
                        class="nav-link text-nowrap"
                        :class="{ 'active text-info font-weight-bold': entryType == item.value, 'disabled': !status.save }"
                        :aria-current="entryType == item.value ? 'page' : null"
                        :aria-disabled="!status.save"
                        @click.prevent="changeEntryType(item.value)"
                    >
                        {{ item.name }}
                    </a>
                </li>
            </ul>
        </div>
        <b-form-group v-if="entryType == 'user'" :label="$t('module.user')" label-cols="3" label-align="left" class="position-relative overflow-visible fade-in">
            <multiselect
                v-model="createForm.user"
                track-by="value"
                label="text"
                :placeholder="$t('message.pleaseSelectX', [$t('module.user')])"
                :options="userEmailDdl"
                :searchable="true"
                :allow-empty="true"
                :class="{ 'border border-danger': submitted && !$v.createForm.user.required }"
            />
            <div v-if="submitted && !$v.createForm.user.required" class="invalid-feedback d-block text-right">
                {{ $t('message.requiredX', [$t('module.user')]) }}
            </div>
        </b-form-group>
        <div class="fade-in" v-else>
            <b-form-group :label="$t('label.name')" label-cols="3" label-align="left">
                <b-form-input v-model.trim="createForm.name" :class="{ 'is-invalid': submitted && !$v.createForm.name.required }" autocomplete="off" />
                <b-form-invalid-feedback v-if="submitted && !$v.createForm.name.required" class="text-right">{{ $t('message.requiredX', [$t('label.name')]) }}</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group :label="$t('label.email')" label-cols="3" label-align="left">
                <b-form-input v-model.trim="createForm.email" :class="{ 'is-invalid': submitted && $v.createForm.email.$error }" autocomplete="off" />
                <b-form-invalid-feedback v-if="submitted && $v.createForm.email.$error" class="text-right">
                    <span v-if="!$v.createForm.email.required">{{ $t('message.requiredX', [$t('label.email')]) }}</span>
                    <span v-else>{{ $t('message.invalidX', [$t('label.email')]) }}</span>
                </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group :label="$t('label.phone')" label-cols="3" label-align="left">
                <vue-tel-input
                    v-model.trim="createForm.phoneNumber"
                    :defaultCountry="createForm.phoneIso || 'MY'"
                    :autoDefaultCountry="false"
                    :autoFormat="false"
                    :class="{ 'border border-danger': submitted && $v.createForm.phoneNumber.$error }"
                    autocomplete="off"
                    v-on:country-changed="(country) => phoneCountryChanged(country)"
                />
                <div v-if="submitted && ($v.createForm.phoneNumber.$error || $v.createForm.phoneCode.$error || $v.createForm.phoneIso.$error)" class="invalid-feedback d-block text-right">
                    {{ $t('message.requiredX', [$t('label.phone')]) }}
                </div>
            </b-form-group>
        </div>
        <hr>
        <div class="row">
            <div class="col-md-6">
                <b-form-group label="Extension" label-cols="6" label-align="left">
                    <b-form-input v-model.trim="createForm.ext" autocomplete="off" />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group label="DID call" label-cols="6" label-align="left">
                    <b-form-input v-model.trim="createForm.didCall" autocomplete="off" />
                </b-form-group>
            </div>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button type="button" variant="danger" class="mr-2 col-3" :disabled="!status.save" @click="$emit('done')">
                {{ $t('label.cancel') }}
            </b-button>
            <b-button type="submit" variant="info" class="col-3" :disabled="!status.save || !loaded">
                <span v-if="status.save">{{ itemUUID ? $t('label.updateX', []) : $t('label.createX', []) }}</span>
                <b-spinner v-else small />
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-3">
        <b-spinner class="m-5" variant="info" role="status" />
    </div>
</template>
<script>
import Multiselect from "vue-multiselect";
import { VueTelInput } from 'vue-tel-input';
import { required, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { phoneExtItemMethods, lookupMethods } from "@/state/api";
import { PHONE_EXT_ITEM_MODULE } from "@/state/api/phoneExtItem.state";

export default {
    components: { VueTelInput, Multiselect },
    props: {
        departmentUUID: { type: String, required: true },
        itemUUID: { type: String, default: '' },
        sequence: { type: Number, default: 1 },
    },
    validations() {
        return {
            createForm: this.entryType == 'user' ? {
                user: { required },
            } : {
                name: { required },
                email: { required, email },
                phoneNumber: { required },
                phoneCode: { required },
                phoneIso: { required },
            },
        };
    },
    async created() {
        await this.lookupUserEmail({});
        await this.getItemData();
    },
    data() {
        return {
            entryType: 'user',
            entryTypeDdl: [
                { value: 'user', name: 'Existing User' },
                { value: 'manual', name: 'Enter contact details' },
            ],
            submitted: false,
            loading: false,
            loaded: !this.itemUUID,
            createForm: this.defaultForm(),
        };
    },
    methods: {
        ...phoneExtItemMethods,
        ...lookupMethods,
        changeEntryType(value) {
            if (!this.status.save) return;
            this.entryType = value;
            this.resetValidation();
        },
        resetValidation() {
            this.submitted = false;
            this.$nextTick(() => this.$v.$reset());
        },
        defaultForm() {
            return {
                uuid: '',
                user: null,
                name: '',
                email: '',
                phoneCode: '+60',
                phoneNumber: '',
                phoneIso: 'MY',
                ext: '',
                didCall: '',
                sequence: this.sequence,
            };
        },
        async getItemData() {
            if (this.itemUUID) {
                this.loading = true;
                await this.getPhoneExtItem({uuid: this.itemUUID})
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                user: this.data.user ? {
                                    value: this.data.user.uuid,
                                    text: this.data.user.fullName || [this.data.user.firstName, this.data.user.lastName].filter(Boolean).join(' ') + ' ' + (this.data.user.email ? '(' +this.data.user.email+ ')' : ''),
                                } : null,
                                name: this.data.name,
                                email: this.data.email,
                                phoneCode: this.data.phoneCode,
                                phoneNumber: String(this.data.phoneNumber ?? ''),
                                phoneIso: this.data.phoneIso,
                                ext: this.data.ext,
                                didCall: this.data.didCall,
                                sequence: this.data.sequence,
                            };
                            this.entryType = this.data.user ? 'user' : 'manual';
                            this.loaded = true;
                        }
                    });
                this.loading = false;
            }
        },
        phoneCountryChanged(country) {
            this.createForm.phoneCode = '+' + country.dialCode;
            this.createForm.phoneIso = country.iso2;
        },
        requestCreate() {
            if (!this.status.save || !this.loaded) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) return;
            let data = {
                uuid: this.createForm.uuid,
                phoneExtDepartmentUuid: this.departmentUUID,
                userUuid: this.entryType == 'user' ? this.createForm.user.value : null,
                name: this.entryType == 'manual' ? this.createForm.name : null,
                email: this.entryType == 'manual' ? this.createForm.email : null,
                phoneCode: this.entryType == 'manual' ? this.createForm.phoneCode : null,
                phoneNumber: this.entryType == 'manual' ? this.createForm.phoneNumber : null,
                phoneIso: this.entryType == 'manual' ? this.createForm.phoneIso : null,
                ext: this.createForm.ext,
                didCall: this.createForm.didCall,
                sequence: this.createForm.sequence,
            };
            this.createPhoneExtItem({data})
                .then((result) => this.$nextTick(() => {
                    if (result?.success) {
                        this.$emit('reload');
                        this.$emit('done');
                    }
                }));
        },
        requestUpdate() {
            if (!this.status.save || !this.loaded) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) return;
            let data = {
                uuid: this.createForm.uuid,
                phoneExtDepartmentUuid: this.departmentUUID,
                userUuid: this.entryType == 'user' ? this.createForm.user.value : null,
                name: this.entryType == 'manual' ? this.createForm.name : null,
                email: this.entryType == 'manual' ? this.createForm.email : null,
                phoneCode: this.entryType == 'manual' ? this.createForm.phoneCode : null,
                phoneNumber: this.entryType == 'manual' ? this.createForm.phoneNumber : null,
                phoneIso: this.entryType == 'manual' ? this.createForm.phoneIso : null,
                ext: this.createForm.ext,
                didCall: this.createForm.didCall,
                sequence: this.createForm.sequence,
            };
            this.updatePhoneExtItem({data})
                .then((result) => this.$nextTick(() => {
                    if (result?.success) {
                        this.$emit('reload');
                        this.$emit('done');
                    }
                }));
        },
    },
    computed: {
        userEmailDdl() {
            return this.$store.state.api.lookup.userEmailDdl || [];
        },
        ...mapState(PHONE_EXT_ITEM_MODULE, ['status']),
        data() {
            return this.$store.state.api.phoneExtItem.details;
        },
    },
};
</script>
